import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  {
    id: "full_name",
    overline: "First, introductions",
    question: "What is your name?",
    type: "text",
    placeholder: "Your full name",
  },
  {
    id: "email",
    overline: "Staying in touch",
    question: "What is the best email to reach you?",
    type: "email",
    placeholder: "you@example.com",
  },
  {
    id: "child_age",
    overline: "Your little one",
    question: "How old is your child (or children)?",
    type: "select",
    options: ["12–24 months", "2–3 years old", "4–5 years old", "Multiple children, ages 1–5"],
  },
  {
    id: "why_now",
    overline: "The timing",
    question: "What made you interested in Legacy Language Architecture now?",
    type: "textarea",
    placeholder: "Share what shifted for you recently…",
  },
  {
    id: "heard_moment",
    overline: "The echo",
    question: "What is one everyday moment when you hear yourself saying something you don't want to pass on?",
    type: "textarea",
    placeholder: "e.g., I caught myself saying 'we can't afford that' in the Target aisle…",
  },
  {
    id: "tried_so_far",
    overline: "The work so far",
    question: "What have you already tried or explored around your own beliefs or parenting language?",
    type: "textarea",
    placeholder: "Journaling, therapy, parenting courses, inner child work…",
  },
  {
    id: "six_month_hope",
    overline: "The horizon",
    question: "Six months from now, what would you hope feels different for you and your child?",
    type: "textarea",
    placeholder: "Describe your ideal ordinary day together…",
  },
  {
    id: "questions_for_call",
    overline: "Almost there",
    question: "What questions or concerns would you want to talk through on a call?",
    type: "textarea",
    placeholder: "Anything you'd like to ask Shellie… (optional)",
    optional: true,
  },
  {
    id: "investment_readiness",
    overline: "The investment",
    question: "LLA is a private 6-month partnership with a meaningful investment. If we decide it's a fit, are you open to a conversation about enrolling?",
    type: "radio",
    options: [
      "Yes — I'm open to the conversation",
      "I have questions I'd like to talk through first",
      "Not at this time",
    ],
  },
  {
    id: "coaching_acknowledgement",
    overline: "One last thing",
    question: "I understand that Legacy Language Architecture is parent coaching — not therapy, and not speech therapy for my child.",
    type: "checkbox",
    checkboxLabel: "I understand and acknowledge this",
  },
];

const isValid = (step, value) => {
  if (step.optional) return true;
  if (step.type === "checkbox") return value === true;
  if (step.type === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");
  return Boolean(value && String(value).trim().length > 0);
};

const inputClass =
  "w-full rounded-xl border border-[#E6DEC3] bg-white px-5 py-4 text-base text-[#241E1A] placeholder:text-[#8A7E74]/70 outline-none transition-colors duration-300 focus:border-[#8C4A32] focus:ring-2 focus:ring-[#8C4A32]/15";

export const Application = ({ bookingUrl }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const topRef = useRef(null);

  const step = steps[stepIndex];
  const value = answers[step.id];
  const valid = isValid(step, value);
  const isLast = stepIndex === steps.length - 1;

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [stepIndex, submitted]);

  useEffect(() => {
    if (!submitted || !bookingUrl) return;
    const t = setTimeout(() => {
      window.open(bookingUrl, "_blank", "noopener,noreferrer");
    }, 2600);
    return () => clearTimeout(t);
  }, [submitted, bookingUrl]);

  const setValue = (v) => setAnswers((a) => ({ ...a, [step.id]: v }));

  const next = () => {
    if (!valid) return;
    if (isLast) {
      submit();
      return;
    }
    setDirection(1);
    setStepIndex((i) => i + 1);
  };

  const back = () => {
    if (stepIndex === 0) return;
    setDirection(-1);
    setStepIndex((i) => i - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        full_name: answers.full_name?.trim(),
        email: answers.email?.trim(),
        child_age: answers.child_age,
        why_now: answers.why_now?.trim(),
        heard_moment: answers.heard_moment?.trim(),
        tried_so_far: answers.tried_so_far?.trim(),
        six_month_hope: answers.six_month_hope?.trim(),
        questions_for_call: answers.questions_for_call?.trim() || "",
        investment_readiness: answers.investment_readiness,
        coaching_acknowledgement: answers.coaching_acknowledgement === true,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error("Something went wrong sending your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = () => {
    switch (step.type) {
      case "text":
      case "email":
        return (
          <input
            data-testid={`application-input-${step.id}`}
            type={step.type}
            value={value || ""}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && valid && next()}
            placeholder={step.placeholder}
            className={inputClass}
            autoFocus
          />
        );
      case "textarea":
        return (
          <textarea
            data-testid={`application-input-${step.id}`}
            value={value || ""}
            onChange={(e) => setValue(e.target.value)}
            placeholder={step.placeholder}
            rows={4}
            className={`${inputClass} resize-none`}
            autoFocus
          />
        );
      case "select":
        return (
          <div className="grid gap-3" data-testid={`application-input-${step.id}`}>
            {step.options.map((opt) => (
              <button
                key={opt}
                type="button"
                data-testid={`application-option-${opt.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                onClick={() => setValue(opt)}
                className={`rounded-xl border px-5 py-4 text-left text-base transition-all duration-300 ${
                  value === opt
                    ? "border-[#8C4A32] bg-[#8C4A32] text-[#FBF8F3]"
                    : "border-[#E6DEC3] bg-white text-[#241E1A] hover:border-[#8C4A32]/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        );
      case "radio":
        return (
          <div className="grid gap-3" data-testid={`application-input-${step.id}`}>
            {step.options.map((opt, i) => (
              <button
                key={opt}
                type="button"
                data-testid={`application-investment-option-${i + 1}`}
                onClick={() => setValue(opt)}
                className={`rounded-xl border px-5 py-4 text-left text-base transition-all duration-300 ${
                  value === opt
                    ? "border-[#8C4A32] bg-[#8C4A32] text-[#FBF8F3]"
                    : "border-[#E6DEC3] bg-white text-[#241E1A] hover:border-[#8C4A32]/50"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <button
            type="button"
            data-testid={`application-input-${step.id}`}
            onClick={() => setValue(!value)}
            className={`flex items-center gap-4 rounded-xl border px-5 py-4 text-left text-base transition-all duration-300 ${
              value ? "border-[#8C4A32] bg-[#8C4A32]/5" : "border-[#E6DEC3] bg-white hover:border-[#8C4A32]/50"
            }`}
          >
            <span
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors duration-300 ${
                value ? "bg-[#8C4A32] border-[#8C4A32]" : "border-[#8A7E74]"
              }`}
            >
              {value && (
                <svg className="w-4 h-4 text-[#FBF8F3]" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10.5L8.5 15L16 5.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="text-[#241E1A] font-medium">{step.checkboxLabel}</span>
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <section id="apply" data-testid="application-section" className="py-16 sm:py-24 lg:py-32 bg-[#F4EFE6]">
      <div ref={topRef} className="scroll-mt-24 max-w-2xl mx-auto px-4 sm:px-8">
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="thanks"
              data-testid="thank-you-screen"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl bg-white border border-[#EFE6D8] p-8 sm:p-12 text-center shadow-[0_24px_60px_-24px_rgba(36,30,26,0.3)]"
            >
              <span className="font-serif-display italic text-5xl text-[#C69C6D]">&ldquo;</span>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#241E1A] font-medium leading-snug">
                Your application is in, {answers.full_name?.split(" ")[0]}.
              </h2>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#5C524A]">
                You did the work to change your life. This is how it finally reaches her.
                Shellie personally reviews every application — the next step is to book your
                private fit call.
              </p>
              <a
                data-testid="booking-redirect-link"
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-block rounded-full bg-[#8C4A32] text-[#FBF8F3] px-8 py-4 text-base font-semibold tracking-wide transition-all duration-300 hover:bg-[#733B27] hover:scale-[1.03]"
              >
                Book your fit call with Shellie
              </a>
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#8A7E74]">
                Opening Shellie's calendar for you now…
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              data-testid="application-form-container"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="rounded-2xl bg-white border border-[#EFE6D8] p-6 sm:p-10 shadow-[0_24px_60px_-24px_rgba(36,30,26,0.3)]"
            >
              <Reveal y={16}>
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] text-center">
                  The application
                </p>
                <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl text-[#241E1A] font-medium text-center leading-snug">
                  Tell me a little about you and your child
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#8A7E74] text-center max-w-md mx-auto">
                  LLA is a private 6-month partnership for mothers of children ages 1–5.
                  Applying doesn't enroll you or charge you — it opens the conversation.
                </p>
              </Reveal>

              <div className="mt-8 mb-2 flex items-center gap-3">
                <span className="text-xs font-semibold tracking-widest text-[#8A7E74] uppercase whitespace-nowrap" data-testid="application-step-indicator">
                  {stepIndex + 1} / {steps.length}
                </span>
                <div className="h-1 flex-1 rounded-full bg-[#F4EFE6] overflow-hidden">
                  <motion.div
                    data-testid="application-progress-bar"
                    className="h-full bg-[#8C4A32] rounded-full"
                    animate={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={step.id}
                  custom={direction}
                  initial={{ opacity: 0, x: direction * 48 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -48 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="pt-6"
                >
                  <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C69C6D] mb-2">{step.overline}</p>
                  <h3 className="font-serif-display text-xl sm:text-2xl text-[#241E1A] font-medium leading-snug mb-6">
                    {step.question}
                  </h3>
                  {renderField()}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center justify-between gap-4">
                <button
                  type="button"
                  data-testid="application-step-prev-button"
                  onClick={back}
                  disabled={stepIndex === 0}
                  className="rounded-full px-6 py-3 text-sm font-semibold text-[#8A7E74] transition-colors duration-300 hover:text-[#241E1A] disabled:opacity-0"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid={isLast ? "application-submit-button" : "application-step-next-button"}
                  onClick={next}
                  disabled={!valid || submitting}
                  className="rounded-full bg-[#8C4A32] text-[#FBF8F3] px-8 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-[#733B27] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : isLast ? "Submit my application" : "Continue"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
